import React, { useState, useCallback, useContext, useMemo } from "react";
import JsZip from "jszip";
import FileSaver from "file-saver";
import { withSearch } from "@elastic/react-search-ui";
import { resultFieldCells } from "../config/faq-config-helper";
import appConfig from "../../config";
import { useTranslation } from "react-i18next";
import saveWorkbook from "../../exceljs";

const csvFileName = appConfig.parametricProduct;
const ResultSelectContext = React.createContext({});

function fileNameFromUrl(url) {
  var matches = url.match(/\/([^\/?#]+)[^\/]*$/);
  if (matches.length > 1) {
    return matches[1];
  }
  return null;
}

const download = (url) => {
  return fetch(url)
    .then((resp) => ({
      blob: resp.blob(),
      name: fileNameFromUrl(url),
      valid: resp.status === 404 ? false : true,
    }))
    .catch((e) => ({ valid: false }));
};

const downloadMany = (urls) => {
  return Promise.all(urls.map((url) => download(url)));
};

const downloadByGroup = (urls) => {
  return Promise.all(urls.map(download));
};

const exportZip = (blobs, errorMessage) => {
  const zip = JsZip();
  const validBlobs = blobs.filter(({ valid }) => valid);
  validBlobs.forEach(({ blob, name }) => {
    zip.file(name, blob);
  });

  if (validBlobs.length > 0) {
    zip.generateAsync({ type: "blob" }).then((zipFile) => {
      const currentDate = new Date().getTime();
      const fileName = `${csvFileName}-${currentDate}.zip`;
      return FileSaver.saveAs(zipFile, fileName);
    });
  } else {
    window.alert(errorMessage);
  }
};

const downloadAndZip = (urls, errorMessage) => {
  return downloadByGroup(urls, 5).then((files) =>
    exportZip(files, errorMessage),
  );
};

const sanitizeCSVText = (csvText = "") =>
  csvText.replace(/\r?\n|\r/g, " ").replace(/,/g, "");

const downloadCSV = (csvText, exportedFilenmae) => {
  var blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });

  if (window.navigator.msSaveBlob) {
    // IE 10+
    window.navigator.msSaveBlob(blob, exportedFilenmae);
  } else {
    var link = window.document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = window.URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilenmae);
      link.style.visibility = "hidden";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  }
};
export const ResultSelectProvider = withSearch(({ results }) => ({ results }))(
  ({ children, results }) => {
    const [selectedResultIds, setSelectedResultIds] = useState({});
    const { t } = useTranslation();
    // const [isAllResultSelected, setIsAllResultsSelected] = useState(false);

    const setIsAllResultsSelected = useCallback(() => {
      setSelectedResultIds(
        results.reduce((acc, result) => {
          acc[result.id.raw] = true;
          return acc;
        }, {}),
      );
    }, [results]);

    const resetAllSelected = useCallback(() => {
      setSelectedResultIds({});
    }, []);

    const isAllResultSelected = useMemo(() => {
      return (
        results.length ===
        results
          .map(({ id }) => id.raw)
          .filter((resultId) => selectedResultIds[resultId]).length
      );
    }, [results, selectedResultIds]);

    const isSelectedResultId = useCallback(
      (resultId) => !!selectedResultIds[resultId],
      [selectedResultIds, isAllResultSelected],
    );

    const setSelectedResultId = useCallback(
      (resultId) => {
        setSelectedResultIds((results) => ({
          ...results,
          [resultId]: true,
        }));
      },
      [setSelectedResultIds],
    );
    const removeSelectedResultId = useCallback(
      (resultId) => {
        setSelectedResultIds((results) => ({
          ...results,
          [resultId]: false,
        }));
      },
      [setSelectedResultIds],
    );
    const toggleSelection = useCallback(
      (resultId) => {
        setSelectedResultIds((results) => ({
          ...results,
          [resultId]: !results[resultId],
        }));
      },
      [setSelectedResultIds],
    );

    const downloadCatalog = useCallback(async () => {
      const selectedResults = Object.entries(selectedResultIds)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => key);

      const selected = isAllResultSelected
        ? results
        : results.filter((result) => selectedResults.includes(result.id.raw));

      const dataLinks = Array.from(
        new Set(
          selected
            .filter(({ data_sheet_link: { raw } }) => raw)
            .map(({ data_sheet_link: { raw } }) => raw),
        ),
      );
      const errorMessage = t("No file to download!");

      await downloadAndZip(dataLinks, errorMessage);
    }, [results, selectedResultIds, isAllResultSelected, t]);

    const downloadSelectedCSV = useCallback(() => {
      const selectedResults = Object.entries(selectedResultIds)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => key);

      const csvMapping = (
        isAllResultSelected
          ? results
          : results.filter((result) => selectedResults.includes(result.id.raw))
      ).map((result) => {
        const csvFields = resultFieldCells.filter((field) => field.isCSV);
        return csvFields.reduce((acc, csvField) => {
          const title = csvField["csvTitle"] || csvField["label"];
          const value = csvField.csvFormat
            ? csvField.csvFormat({ result })
            : result[csvField["field"]]?.raw;
          acc[t(title).toString()] = (value || "").toString().replace(/,/g, "");
          return acc;
        }, {});
      });

      if (csvMapping.length === 0) {
        return;
      }

      const headers = Object.keys(csvMapping[0]);
      // const csvValues = csvMapping.map(c =>
      //   Object.values(c)
      //   .map(sanitizeCSVText)
      // );
      saveWorkbook(headers, csvMapping, `${csvFileName}.xlsx`);
      // const headerText = headers.map(sanitizeCSVText).join(",");
      // const bodyText = csvValues.join("\r\n");
      // downloadCSV(headerText + "\r\n" + bodyText, csvFileName + ".csv");
    }, [results, selectedResultIds, isAllResultSelected, t]);

    return (
      <ResultSelectContext.Provider
        value={{
          selectedResultIds,
          setSelectedResultIds,
          isAllResultSelected,
          setIsAllResultsSelected,
          resetAllSelected,
          toggleSelection,
          isSelectedResultId,
          setSelectedResultId,
          removeSelectedResultId,
          downloadSelectedCSV,
          downloadCatalog,
        }}
      >
        {children}
      </ResultSelectContext.Provider>
    );
  },
);

/**
 * 
 * @param {function} mapContextToProps 
 * @returns 
 * Example: 
 * const mapContextToProps = ({
        selectedResultIds,
        setSelectedResultIds,
        isAllResultSelected,
        setIsAllResultsSelected,
        isSelectedResultId,
        setSelectedResultId,
        removeSelectedResultId
      }) => ({
        selectedResultIds,
        setSelectedResultIds,
        isAllResultSelected,
        setIsAllResultsSelected,
        isSelectedResultId,
        setSelectedResultId,
        removeSelectedResultId
      })

      export withSelectedResult(mapContextToProps)(SomeComponent)
 */

export const withSelectedResult = (mapContextToProps) => {
  if (!mapContextToProps) {
    throw new Error(
      "withSelectedResult requires a function to be provided which returns an object with at least one value.",
    );
  }

  return (Component) => (props) => {
    const resultContext = useContext(ResultSelectContext);
    return <Component {...props} {...mapContextToProps(resultContext)} />;
  };
};

export default ResultSelectContext;
